// Error tracker maison — Sentry-light qui logge dans public.error_logs.
//
// Activé dès l'import. Capture :
//  - window.onerror (erreurs JS sync)
//  - window.onunhandledrejection (Promise rejetées non catch)
//  - reportError(err, context) appelé manuellement depuis le code
//
// Volontairement léger : pas de breadcrumbs auto, pas d'instrumentation,
// pas de sourcemaps remote. Pour un vrai SaaS post-pilote, basculer sur
// @sentry/react (bundler-aware, transactions, replays, etc.).

import { supabase } from './supabase'

interface ErrorContext {
  level?: 'error' | 'warning' | 'fatal'
  context?: Record<string, unknown>
}

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev'

/**
 * Évite la boucle infinie : si l'INSERT error_logs échoue, on ne re-capture pas
 * sa propre erreur (qui re-tenterait l'INSERT, etc.).
 */
let isReporting = false

const buildPayload = async (
  err: unknown,
  ctx: ErrorContext,
): Promise<Record<string, unknown> | null> => {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : ((err as { message?: string })?.message ?? 'Unknown error')
  const stack = err instanceof Error ? (err.stack ?? null) : null
  if (!message) return null

  // Charger le profil courant si possible (best-effort)
  let userId: string | null = null
  let userRole: string | null = null
  let pharmacyId: string | null = null
  try {
    const { data } = await supabase.auth.getUser()
    if (data.user?.id) {
      userId = data.user.id
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role, pharmacy_id')
        .eq('id', data.user.id)
        .maybeSingle()
      if (profile) {
        userRole = (profile as { role: string }).role
        pharmacyId = (profile as { pharmacy_id: string | null }).pharmacy_id
      }
    }
  } catch {
    // best effort
  }

  return {
    user_id: userId,
    user_role: userRole,
    pharmacy_id: pharmacyId,
    level: ctx.level ?? 'error',
    message: message.slice(0, 1000),
    stack: stack ? stack.slice(0, 4000) : null,
    url: window.location.href,
    user_agent: navigator.userAgent.slice(0, 300),
    context: ctx.context ?? null,
    released: APP_VERSION,
  }
}

export const reportError = async (err: unknown, ctx: ErrorContext = {}): Promise<void> => {
  if (isReporting) return
  isReporting = true
  try {
    const payload = await buildPayload(err, ctx)
    if (!payload) return
    // Ne pas attendre — fire-and-forget, on ne veut pas ralentir l'UX
    void supabase
      .from('error_logs')
      .insert(payload as never)
      .then(() => {
        /* swallow */
      })
  } catch {
    // jamais bloquer l'app
  } finally {
    setTimeout(() => {
      isReporting = false
    }, 100)
  }
}

let installed = false

export const installGlobalErrorTracker = (): void => {
  if (installed) return
  installed = true

  window.addEventListener('error', (event) => {
    void reportError(event.error ?? event.message, {
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    void reportError(event.reason, {
      context: { kind: 'unhandled_rejection' },
    })
  })
}
